import React, { useState, useEffect } from 'react';
import ReusableTable, { TableColumn } from '../components/adminTable';
import { Mail, CheckCircle2, XCircle, Send, Paperclip, X, Loader2 } from 'lucide-react';
import { Button, FormGroup, FormLabel, FormControl } from '../components/adminModals';
import Drawer from '../../sharedComponents/Drawer';
import { submissionService, approvalService, eventCycleService, notificationService, Category, CycleEvent, FinalDecisionSubmission } from '../services/adminAuth';
import Swal from 'sweetalert2';

interface CommunicationAuthor {
    id: string;
    fullName: string;
    email: string;
    status: 'Passed' | 'Failed';
    manuscriptTitle: string;
    submissionDate: string;
}

function AuthorCommunications() {
    const [authors, setAuthors] = useState<CommunicationAuthor[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // Filter states
    const [categories, setCategories] = useState<Category[]>([]);
    const [activeCycle, setActiveCycle] = useState<CycleEvent | null>(null);
    const [filters, setFilters] = useState({
        categoryId: 0,
        applicationType: 0,
        finalDecision: 0 // 0 for Passed, 1 for Failed
    });

    // Drawer state
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [emailForm, setEmailForm] = useState({
        subject: '',
        body: ''
    });
    const [attachments, setAttachments] = useState<File[]>([]);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                const [catRes, cycleRes] = await Promise.all([
                    submissionService.getAllCategories(true),
                    eventCycleService.getActiveCycleEvent()
                ]);

                if (catRes.isSuccess) setCategories(catRes.data);
                if (cycleRes.isSuccess) setActiveCycle(cycleRes.data);
            } catch (err) {
                } finally {
                setLoading(false);
            }
        };
        init();
    }, []);

    useEffect(() => {
        if (activeCycle) {
            fetchAuthors();
        }
    }, [activeCycle, filters]);

    const fetchAuthors = async () => {
        if (!activeCycle) return;

        setLoading(true);
        try {
            const payload = {
                categoryId: filters.categoryId,
                cycleId: activeCycle.cycle.id,
                applicationType: filters.applicationType,
                finalDecision: filters.finalDecision
            };

            const response = await approvalService.getFinalDecision(payload);
            if (response.isSuccess && response.data) {
                // Map FinalDecisionSubmission to CommunicationAuthor
                const mappedAuthors: CommunicationAuthor[] = response.data.map(item => ({
                    id: item.submission?.author?.userId || item.id,
                    fullName: item.submission?.author?.person?.fullName || 'N/A',
                    email: item.submission?.author?.person?.email || 'N/A',
                    status: item.finalDecision === 0 ? 'Passed' : 'Failed',
                    manuscriptTitle: item.submission?.title || 'N/A',
                    submissionDate: item.submission?.submittedAt
                        ? new Date(item.submission.submittedAt).toLocaleDateString()
                        : 'N/A'
                }));
                setAuthors(mappedAuthors);
            } else {
                setAuthors([]);
            }
        } catch (err) {
            setAuthors([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setSelectedIds(authors.map(a => a.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id: string) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const handleSendEmail = async (e: React.FormEvent) => {
        e.preventDefault();
        if (selectedIds.length === 0) return;

        const confirm = await Swal.fire({
            title: 'Send Bulk Email?',
            text: `You are about to send this email to ${selectedIds.length} authors.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#15803D',
            confirmButtonText: 'Yes, Send'
        });

        if (!confirm.isConfirmed) return;

        setIsSending(true);
        try {
            const formData = new FormData();

            // Get emails of selected authors
            const selectedEmails = authors
                .filter(a => selectedIds.includes(a.id))
                .map(a => a.email)
                .join(',');

            formData.append('To', selectedEmails);
            formData.append('Subject', emailForm.subject);
            formData.append('Body', emailForm.body);

            if (attachments.length > 0) {
                attachments.forEach(file => {
                    formData.append('Attachments', file);
                });
            }

            const response = await notificationService.sendEmailMessages(formData);

            if (response.isSuccess) {
                Swal.fire({
                    icon: 'success',
                    title: 'Sent!',
                    text: `Email sent successfully to ${selectedIds.length} authors.`,
                    confirmButtonColor: '#15803D'
                });
                setIsDrawerOpen(false);
                setEmailForm({ subject: '', body: '' });
                setAttachments([]);
                setSelectedIds([]);
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Failed',
                    text: response.message || 'Operation failed',
                    confirmButtonColor: '#15803D'
                });
            }
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'An unexpected error occurred while sending emails.',
                confirmButtonColor: '#15803D'
            });
        } finally {
            setIsSending(false);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setAttachments(prev => [...prev, ...Array.from(e.target.files!)]);
        }
    };

    const removeAttachment = (index: number) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const columns: TableColumn<CommunicationAuthor>[] = [
        {
            key: 'selection',
            label: '',
            render: (_, row) => (
                <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    checked={selectedIds.includes(row.id)}
                    onChange={() => handleSelectOne(row.id)}
                />
            ),
            className: 'w-10'
        },
        {
            key: 'fullName',
            label: 'Author Name',
            render: (value, row) => (
                <div>
                    <div className="font-medium text-gray-900">{value}</div>
                    <div className="text-xs text-gray-500">{row.email}</div>
                </div>
            )
        },
        {
            key: 'manuscriptTitle',
            label: 'Manuscript Title',
            render: (value) => <span className="text-sm text-gray-700 line-clamp-1">{value}</span>
        },
        {
            key: 'status',
            label: 'Status',
            render: (value) => (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${value === 'Passed' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                    }`}>
                    {value === 'Passed' ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                    {value}
                </span>
            )
        },
        {
            key: 'submissionDate',
            label: 'Submitted On',
            render: (value) => <span className="text-sm text-gray-500">{value}</span>
        }
    ];

    const filterPanel = (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">Thematic Area</label>
                    <select
                        value={filters.categoryId}
                        onChange={(e) => setFilters(prev => ({ ...prev, categoryId: parseInt(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none"
                    >
                        <option value={0}>All Categories</option>
                        {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">Application Type</label>
                    <select
                        value={filters.applicationType}
                        onChange={(e) => setFilters(prev => ({ ...prev, applicationType: parseInt(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none"
                    >
                        <option value={0}>All Types</option>
                        <option value={1}>Book</option>
                        <option value={2}>Journal Article</option>
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">Approval Status</label>
                    <select
                        value={filters.finalDecision}
                        onChange={(e) => setFilters(prev => ({ ...prev, finalDecision: parseInt(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none"
                    >
                        <option value={0}>Passed (Approved)</option>
                        <option value={1}>Failed (Rejected)</option>
                    </select>
                </div>
            </div>
            <button
                onClick={() => setFilters({ categoryId: 0, applicationType: 0, finalDecision: 0 })}
                className="mt-4 text-xs text-green-700 hover:underline font-medium"
            >
                Clear all filters
            </button>
        </div>
    );

    return (
        <div className="w-full">
            {/* Page Header */}
            <div className="mb-6 flex flex-col md:flex-row justify-between items-start gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Author Communications</h1>
                    <p className="text-gray-500">Send bulk emails and updates to authors based on their submission status.</p>
                </div>

                {selectedIds.length > 0 && (
                    <div className="flex items-center gap-4 animate-in fade-in slide-in-from-right-4 duration-300">
                        <span className="text-sm font-medium text-gray-600">
                            {selectedIds.length} author{selectedIds.length > 1 ? 's' : ''} selected
                        </span>
                        <Button
                            variant="success"
                            onClick={() => setIsDrawerOpen(true)}
                            className="flex items-center gap-2 px-6 shadow-lg"
                        >
                            <Mail className="w-4 h-4" />
                            Send bulk Mail
                        </Button>
                    </div>
                )}
            </div>

            {/* Selection Checkbox for "Select All" - rendered outside table because ReusableTable header is static */}
            <div className="bg-white p-4 border-b border-gray-100 flex items-center gap-3">
                <input
                    type="checkbox"
                    id="selectAll"
                    className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    checked={authors.length > 0 && selectedIds.length === authors.length}
                    onChange={handleSelectAll}
                />
                <label htmlFor="selectAll" className="text-sm font-medium text-gray-600 cursor-pointer">
                    Select all on this page
                </label>
            </div>

            <ReusableTable
                title="Authors List"
                subTitle="Select authors to communicate with"
                columns={columns}
                data={authors}
                loading={loading}
                showSearch={true}
                showFilter={true}
                filterPanel={filterPanel}
                onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
                isFilterOpen={isFilterOpen}
                emptyMessage="No authors found matching the criteria."
            />

            <Drawer
                isOpen={isDrawerOpen}
                onClose={() => !isSending && setIsDrawerOpen(false)}
                title="Compose Bulk Email"
                width="max-w-2xl"
            >
                <form onSubmit={handleSendEmail} className="space-y-6">
                    {/* Recipients Summary */}
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                        <div className="text-sm font-semibold text-blue-900 mb-2 flex items-center justify-between">
                            <span>Recipients ({selectedIds.length})</span>
                            {selectedIds.length > 5 && <span className="text-xs font-normal text-blue-600 italic">Showing first 5...</span>}
                        </div>
                        <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto pr-2">
                            {authors
                                .filter(a => selectedIds.includes(a.id))
                                .slice(0, 5) // Show first 5 in detail
                                .map(author => (
                                    <div key={author.id} className="flex items-center gap-1.5 bg-white border border-blue-200 px-2.5 py-1 rounded-md shadow-sm group">
                                        <div className="flex flex-col">
                                            <span className="text-xs font-semibold text-gray-800 leading-tight">{author.fullName}</span>
                                            <span className="text-[10px] text-gray-500 leading-tight">{author.email}</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleSelectOne(author.id)}
                                            className="text-gray-400 hover:text-red-500 transition-colors ml-1"
                                        >
                                            <X size={12} />
                                        </button>
                                    </div>
                                ))}
                            {selectedIds.length > 5 && (
                                <div className="flex items-center px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-md">
                                    + {selectedIds.length - 5} more recipients
                                </div>
                            )}
                        </div>
                    </div>

                    <FormGroup>
                        <FormLabel required>Subject</FormLabel>
                        <FormControl
                            type="text"
                            placeholder="Enter email subject"
                            value={emailForm.subject}
                            onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                            required
                        />
                    </FormGroup>

                    <FormGroup>
                        <FormLabel required>Message Body</FormLabel>
                        <textarea
                            className="w-full min-h-[250px] p-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all"
                            placeholder="Enter your message here..."
                            value={emailForm.body}
                            onChange={(e) => setEmailForm(prev => ({ ...prev, body: e.target.value }))}
                            required
                        />
                    </FormGroup>

                    {/* Attachments */}
                    <div>
                        <FormLabel>Attachments</FormLabel>
                        <div className="mt-2 space-y-2">
                            <label className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer transition-colors">
                                <Paperclip className="w-4 h-4" />
                                <span>Upload File</span>
                                <input type="file" className="hidden" multiple onChange={handleFileChange} />
                            </label>

                            <div className="grid grid-cols-1 gap-2 mt-2">
                                {attachments.map((file, index) => (
                                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-100 group">
                                        <div className="flex items-center gap-2 overflow-hidden">
                                            <Paperclip className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="text-xs text-gray-600 truncate">{file.name}</span>
                                            <span className="text-xs text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeAttachment(index)}
                                            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
                        <Button
                            variant="white"
                            onClick={() => setIsDrawerOpen(false)}
                            disabled={isSending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="success"
                            type="submit"
                            onClick={() => undefined}
                            disabled={isSending || !emailForm.subject || !emailForm.body}
                            className="min-w-[140px]"
                        >
                            {isSending ? (
                                <span className="flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Sending...
                                </span>
                            ) : (
                                <span className="flex items-center gap-2">
                                    <Send className="w-4 h-4" />
                                    Send Email
                                </span>
                            )}
                        </Button>
                    </div>
                </form>
            </Drawer>
        </div>
    );
}

export default AuthorCommunications;
